import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react'
import { supabase } from '../lib/supabase'

const forgotPasswordSchema = z.object({
  email: z.string().email('请输入有效的邮箱地址')
})

type ForgotPasswordFormSchema = z.infer<typeof forgotPasswordSchema>

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotPasswordFormSchema>({
    resolver: zodResolver(forgotPasswordSchema)
  })

  const onSubmit = async (data: { email: string }) => {
    setLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) {
        console.debug('Password reset error:', error.message)
        setError(error.message)
      } else {
        setSuccess(true)
      }
    } catch (err) {
      console.debug('Password reset exception:', err)
      setError('发送重置密码邮件失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 animate-fadein">
        <div className="max-w-md w-full">
          <div className="card text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-400 mb-4" />
            <h2 className="text-3xl font-bold text-gradient mb-2">
              邮件已发送
            </h2>
            <p className="text-white/70 mb-6">
              我们已向您的邮箱发送了密码重置链接，请检查您的邮箱并点击链接重置密码。
            </p>
            <Link
              to="/login"
              className="btn-primary inline-flex items-center"
            >
              返回登录页面
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 animate-fadein">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="mb-6">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-violet-400 hover:text-violet-300 transition-colors mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              返回登录
            </Link>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gradient mb-2">
                重置密码
              </h2>
              <p className="text-white/70">
                请输入您的邮箱地址，我们将发送密码重置链接给您。
              </p>
            </div>
          </div>
          
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                邮箱地址
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-white/50" />
                </div>
                <input
                  {...register('email')}
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="w-full pl-10 pr-3 py-3"
                  placeholder="请输入邮箱地址"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                ) : (
                  '发送重置链接'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
} 